"use client";

import { ArrowRight, TrendingUp } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function StrategicVisionHero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const credentials = ["Intelligence Digitale", "Neuromarketing B2B", "Logistica Export", "Architettura Web"];
  return <section data-section-id="238" id="strategic-vision-hero" className="bg-background text-foreground relative overflow-hidden">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "radial-gradient(var(--foreground) 1px, transparent 1px)",
      backgroundSize: "3px 3px"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 pt-20 pb-12 sm:px-8 lg:px-12 lg:pt-28 lg:pb-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-7 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-gray">
              <span className="inline-block h-2 w-2 rounded-md" style={{
              backgroundColor: "var(--chart-2)"
            }} aria-hidden="true" />
              <span className="font-default">§ 01 / INFRASTRUTTURA</span>
              <span className="text-muted-foreground/50">/</span>
              <span className="font-default">BLACK LABEL INTELLIGENCE</span>
            </div>

            {}
            <h1 className="font-default mt-8 text-5xl font-bold leading-[1.1] text-foreground sm:text-6xl lg:text-7xl xl:text-8xl uppercase" style={{
            letterSpacing: "-0.02em"
          }}>
              Architetture per la{" "}
              <span className="relative inline-block italic">
                <span style={{
                color: "var(--chart-1)"
              }}>conquista globale</span>
                <svg className="absolute -bottom-2 left-0 h-3 w-full" viewBox="0 0 300 12" preserveAspectRatio="none" fill="none" aria-hidden="true">
                  <path d="M2 8 L 298 8" stroke="var(--chart-1)" strokeWidth="2.5" strokeLinecap="square" fill="none" />
                </svg>
              </span>
              .
            </h1>

            {}
            <p className="font-default mt-8 max-w-[540px] text-lg leading-[1.6] text-foreground">
              Non siamo una web agency. Costruiamo infrastrutture digitali ad altissima conversione 
              fondendo neuromarketing, architettura avanzata e intelligenza logistica per 
              l'espansione commerciale dei leader industriali italiani.
            </p>

            {}
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <Button asChild size="lg" className="h-12 rounded-md px-7 text-sm font-bold bg-primary text-primary-foreground shadow-md hover:shadow-lg transition-shadow">
                <Link to="#">
                  ANALISI STRATEGICA
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>

              <Link to="#" className="group inline-flex items-center gap-2 text-sm font-semibold text-foreground hover:text-accent-foreground transition-colors">
                <span className="border-b border-foreground/40 pb-0.5 group-hover:border-accent-foreground font-default uppercase tracking-wider">
                  IL NOSTRO METODO
                </span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            {}
            <Separator className="mt-12 bg-border" />

            {}
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-gray">
              {credentials.map((cred, i) => <div key={cred} data-index={i} className="flex items-center gap-4">
                  {i > 0 && <span className="text-muted-foreground/40">·</span>}
                  <span className="font-default">{cred}</span>
                </div>)}
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-5 transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative">
              {}
              <div className="relative overflow-hidden rounded-md shadow-xl" style={{
              aspectRatio: "4/5"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1591123/img/industrial_architecture_global_logistics.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1591123/img/industrial_architecture_global_logistics.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1591123/img/industrial_architecture_global_logistics.png-wvc-sizes" alt="High-contrast architectural geometry representing global B2B infrastructure" loading="lazy" decoding="async" className="h-full w-full object-cover" style={{
                filter: "grayscale(1) brightness(0.9)"
              }} />
                {}
                <div className="absolute inset-0 mix-blend-overlay opacity-30" style={{
                background: "linear-gradient(135deg, var(--chart-3) 0%, transparent 50%, var(--chart-2) 100%)"
              }} aria-hidden="true" />
              </div>

              {}
              <div className="absolute -bottom-6 -left-4 sm:left-6 max-w-[260px] rounded-md border border-border bg-card/90 p-5 shadow-lg backdrop-blur-md">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-gray">
                      Export Growth
                    </div>
                    <div className="font-default mt-1.5 text-2xl font-bold text-foreground uppercase" style={{
                    letterSpacing: "-0.02em"
                  }}>
                      +314%
                    </div>
                    <div className="mt-0.5 text-[11px] text-foreground font-default">
                      incremento lead B2B estero
                    </div>
                  </div>
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md" style={{
                  backgroundColor: "var(--chart-2)",
                  color: "white"
                }}>
                    <TrendingUp className="h-4 w-4" />
                  </div>
                </div>

                {}
                <svg className="mt-3 w-full" viewBox="0 0 200 36" preserveAspectRatio="none" fill="none" aria-hidden="true">
                  <path d="M0 30 L 40 28 L 80 22 L 120 18 L 160 10 L 200 2" stroke="var(--chart-2)" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" fill="none" />
                  <path d="M0 30 L 40 28 L 80 22 L 120 18 L 160 10 L 200 2 L 200 36 L 0 36 Z" fill="var(--chart-2)" fillOpacity="0.1" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {}
        <Separator className="mt-24 bg-border" />
      </div>
    </section>;
}